import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { ArrowLeftLine } from '@archipro-design/icons';

import { useAtomValue } from 'jotai';
import { useTracker } from '@archipro-website/tracker';
import { TwoPaneModal } from '@rocco/components/two-pane-modal';
import { uiContextAtomDisplayMode } from '@rocco/states/uiContextAtomDisplayMode';
import {
    useAuthGateSegment,
    type AuthGateSegment,
} from '~/modules-rocco/user/auth-gate/use-auth-gate-segment';
import { trackAuthGateActionResumed } from '~/modules-rocco/user/auth-gate/auth-gate-events';
import { useSocialAuthReturn } from '~/modules-rocco/user/auth-gate/use-social-auth-return';
import { useUserLoggedIn } from '@modules/user';
import type { PublicUser } from '~/routes/remix-api.user.public-user';
import { messageSubject } from '~/modules/enquiry/bulk/bulk-enquiry-copy';
import {
    trackBulkEnquiryFormOpened,
    trackBulkEnquiryReviewOpened,
} from '~/modules/enquiry/bulk/bulk-enquiry-events';
import {
    hasStoredBulkSelection,
    useBulkEnquiryMode,
    useBulkEnquirySelection,
} from '~/modules/enquiry/bulk/bulk-enquiry-state';
import {
    useEnquiryEvent,
    useEnquirySession,
} from '~/modules/enquiry/hooks/use-enquiry';
import { useSendBlock } from '../use-send-block';
import { BulkEnquirySuccess } from './BulkEnquirySuccess';
import { BulkSelectionPane } from './BulkSelectionPane';
import { EnquiryGateForm } from './EnquiryGateForm';
import { GateStickyFooter } from '~/modules-rocco/user/component/auth-modal/GateStickyFooter';
import { toast } from 'sonner';
import {
    assignDocumentLocation,
    reloadDocument,
} from '~/modules/error/util/document-navigation';

type GateStep = 'form' | 'success';

/**
 * The sheet has no left pane to hold the list beside the form, so on mobile the
 * form step is split in two: review the list, then write the enquiry.
 */
type MobileFormStep = 'list' | 'form';

/** Names this gate on every funnel event as well as selecting its intent copy. */
const BULK_ENQUIRY_GATE_AUTH_SOURCE = 'bulkEnquiry' as const;

interface BulkEnquiryGateModalProps {
    open: boolean;
    onClose: () => void;
}

/**
 * Bulk enquiry in the revamped two-pane gate. The left pane carries the
 * selection itself rather than brand imagery, so the list the island has been
 * collecting stays in view while the enquiry is written — there is no separate
 * review step to step through first.
 *
 * Steps: form → (guest) auth gate → success. A guest submit hits the auth wall
 * and switches to the in-pane AuthPanel; the primed post-login auto-submit
 * (useEnquiryAutoSubmit) re-sends the whole selection, so every recipient gets
 * exactly one enquiry whichever way the user authenticated.
 */
export const BulkEnquiryGateModal = ({
    open,
    onClose,
}: BulkEnquiryGateModalProps) => {
    const { isMobile } = useAtomValue(uiContextAtomDisplayMode);
    const { items, supplierCount, remove } = useBulkEnquirySelection();
    const { exit } = useBulkEnquiryMode();
    const {
        state,
        fetcher,
        enquiryAutoSubmit,
        isAutoSubmitted,
        autoSubmitDelay,
        setEnquiryAutoSubmit,
        setAutoSubmitDelay,
    } = useEnquirySession();
    const { isLoggedIn } = useUserLoggedIn();
    const tracker = useTracker();

    const [step, setStep] = useState<GateStep>('form');
    const [mobileStep, setMobileStep] = useState<MobileFormStep>('list');
    const [gatePublicUser, setGatePublicUser] = useState<PublicUser>();
    const [authedInModal, setAuthedInModal] = useState(false);
    /** Read by `handleSuccess`, which is memoised without `authedInModal`. */
    const authedInModalRef = useRef(false);
    authedInModalRef.current = authedInModal;
    /** The in-modal auth step completed a signup (vs a login). */
    const [authedIsSignUp, setAuthedIsSignUp] = useState(false);
    /**
     * The far side of a social round-trip this gate started. Single use — see
     * {@link useSocialAuthReturn}; the modal outlives the journey it describes.
     */
    const {
        take: takeSocialAuth,
        spend: spendSocialAuth,
        seen: socialAuth,
    } = useSocialAuthReturn(BULK_ENQUIRY_GATE_AUTH_SOURCE);
    const socialSignUpReturn = socialAuth.signUpReturn;

    const gateRef = useRef<AuthGateSegment | null>(null);
    /** This gate has been open at least once since the last close. */
    const hasOpenedRef = useRef(false);
    const isOpenRef = useRef(open);
    isOpenRef.current = open;
    // `handleClose` is defined below; a ref keeps the empty-list effect off its
    // identity, which changes with every step transition.
    const handleCloseRef = useRef<() => void>(() => {});

    // The post-login auto-submit empties the selection as part of sending it
    // (useEnquiryAutoSubmit resets bulk mode on success), so by the time the
    // success screen renders there is nothing left to count. Remember the last
    // real figure while the list still exists.
    const sentSupplierCountRef = useRef(supplierCount);
    const sentItemsRef = useRef(items);
    if (supplierCount > 0) sentSupplierCountRef.current = supplierCount;
    if (items.length > 0) sentItemsRef.current = items;

    // Desktop folds review into the form's selection pane, so opening the gate
    // IS reaching the form. The mobile sheet reviews first, so there each step
    // is its own funnel stage, reported once per arrival.
    const funnelStep = isMobile && mobileStep === 'list' ? 'review' : 'form';
    const reportedFunnelStepRef = useRef<typeof funnelStep | null>(null);
    useEffect(() => {
        if (!open) {
            reportedFunnelStepRef.current = null;
            return;
        }
        if (reportedFunnelStepRef.current === funnelStep) return;
        reportedFunnelStepRef.current = funnelStep;
        const counts = { itemCount: items.length, supplierCount };
        if (funnelStep === 'review') {
            trackBulkEnquiryReviewOpened(tracker, counts);
            return;
        }
        trackBulkEnquiryFormOpened(tracker, counts);
    }, [open, funnelStep, items.length, supplierCount, tracker]);

    // Mirrors the single-enquiry gate: the guest gate leaves `enquiryAutoSubmit`
    // primed while the auth step shows, held back by `autoSubmitDelay`. Only
    // treat it as actually sending once that clears, and never for a logged-out
    // visitor carrying a stale flag — the resubmit needs a session.
    const isResuming =
        isLoggedIn &&
        ((enquiryAutoSubmit && !autoSubmitDelay) ||
            (isAutoSubmitted && state !== 'idle'));

    // Secure Sign-In sits inside the auth segment, so the pipeline is unchanged:
    // [list + message] → [auth] → [sent]. The resubmit keeps running behind the
    // ask, so only what the user sees is sequenced.
    const stepRef = useRef<GateStep>('form');
    stepRef.current = step;
    /**
     * The send has landed but the confirmation is still owed, because the
     * Secure Sign-In ask holds the surface.
     *
     * Mirrored as state, not kept as a ref alone: the send empties the
     * selection, and the modal's `open` expression is driven by the list. A ref
     * does not re-render, so the emptied list would close the modal out from
     * under the ask — unmounting the very component whose `onResolved` is the
     * only thing that can show the confirmation.
     */
    const [pendingSuccess, setPendingSuccess] = useState(false);
    const pendingSuccessRef = useRef(false);
    const markPendingSuccess = useCallback((pending: boolean) => {
        pendingSuccessRef.current = pending;
        setPendingSuccess(pending);
    }, []);

    const handleSuccess = useCallback(() => {
        if (!isOpenRef.current) return;
        const askOnScreen = gateRef.current?.step === 'secure-sign-in';
        const data = fetcher.data;
        const generatedMember = !!(
            data &&
            'success' in data &&
            data.data?.__typename === 'CreateEnquiryResult' &&
            data.data.GeneratedMember
        );
        // A send that followed an auth step in this gate is a resumed action,
        // and the only place the resume is known to have landed. The count is
        // what the fan-out actually was, so bulk cannot be read as one enquiry.
        //
        // Reported BEFORE the Secure Sign-In branch below: the resubmit runs
        // behind that ask to keep the send instant, so landing while the ask
        // is on screen is the normal path, not an edge case.
        // Spent here, on the one success it describes — see the enquiry gate.
        const fromSocial = takeSocialAuth();
        if (
            askOnScreen ||
            authedInModalRef.current ||
            fromSocial.authReturn ||
            generatedMember
        ) {
            trackAuthGateActionResumed({
                tracker,
                authSource: BULK_ENQUIRY_GATE_AUTH_SOURCE,
                landed: true,
                // The send empties the selection, so read the figures kept
                // from before it landed rather than the now-empty list.
                data: {
                    itemCount: sentItemsRef.current.length,
                    supplierCount: sentSupplierCountRef.current,
                },
            });
        }
        if (askOnScreen) {
            // Already asking (in-modal auth path) — success shows on resolve.
            markPendingSuccess(true);
            return;
        }
        // Social round-trips and auto-created accounts reach here without an
        // in-modal auth step; their "just logged in" moment is this success.
        if (fromSocial.authReturn || generatedMember) {
            markPendingSuccess(true);
            // A generated account is certainly new, and so is a social signup;
            // a social *login* is a returning member, and calling them new
            // re-offers Secure Sign-In on a device that holds their passkey.
            gateRef.current?.enterAuthenticated({
                isNewAccount: generatedMember || fromSocial.signUpReturn,
            });
            return;
        }
        setStep('success');
    }, [fetcher, takeSocialAuth, tracker, markPendingSuccess]);
    // The resubmit came back an error. Without subscribing to failure, a
    // resubmit that failed AFTER the auth segment resolved reported nothing at
    // all — the check in `handleOfferDone` only catches an error already
    // present by then, so late failures silently vanished from the funnel.
    const handleResubmitFailed = useCallback(() => {
        if (!isOpenRef.current) return;
        trackAuthGateActionResumed({
            tracker,
            authSource: BULK_ENQUIRY_GATE_AUTH_SOURCE,
            landed: false,
            data: {
                itemCount: sentItemsRef.current.length,
                supplierCount: sentSupplierCountRef.current,
            },
        });
    }, [tracker]);

    useEnquiryEvent({
        onSuccess: handleSuccess,
        onFailure: handleResubmitFailed,
    });

    // The ask resolved (enrolled, skipped or failed): show success once the
    // enquiries have landed, otherwise fall back to the form as the retry
    // surface — a still-running resubmit renders the sending overlay over it.
    const handleOfferDone = useCallback(() => {
        if (pendingSuccessRef.current) {
            setStep('success');
            return;
        }
        const data = fetcher.data;
        // The guest submit answers `{ error, publicUser }` — the response
        // that opened this gate's auth step in the first place, and it stays
        // in the fetcher until the primed resubmit replaces it. It is not a
        // failed send, so it must not toast as one; without this check the
        // ask resolving before the resubmit landed claimed the enquiries
        // could not be sent moments before they were. A genuine resubmit
        // failure carries no publicUser and still toasts.
        const authRequired = !!(
            data &&
            'publicUser' in data &&
            data.publicUser
        );
        if (
            state === 'idle' &&
            data &&
            'error' in data &&
            data.error &&
            !authRequired
        ) {
            // The failed resume is reported by `handleResubmitFailed`, which
            // catches it whenever it arrives; emitting here too would count
            // one failure twice.
            toast.error('We couldn’t send your enquiries. Please try again.');
        }
        setStep('form');
    }, [fetcher, state]);

    // Fresh open (or after a plain close) starts on the form.
    useEffect(() => {
        if (open) {
            hasOpenedRef.current = true;
            return;
        }
        setStep('form');
        setMobileStep('list');
        setGatePublicUser(undefined);
        setAuthedInModal(false);
        setAuthedIsSignUp(false);
        markPendingSuccess(false);
        // The journey the social return described has ended, whether or not it
        // reached a success, so the signal must not outlive it.
        //
        // Only on a close that FOLLOWS an opening. "Not open" is also the
        // state at first mount, and on a social return the gate has not opened
        // yet — the auto-submit opens it once the session is restored. Spending
        // it there cleared the signal before the send could read it, which
        // silently dropped Secure Sign-In and the new-account success screen
        // from every social login.
        if (hasOpenedRef.current) {
            hasOpenedRef.current = false;
            spendSocialAuth();
        }
        gateRef.current?.reset();
    }, [open, spendSocialAuth, markPendingSuccess]);

    // Unticking the last row would otherwise only hide the dialog: the `open`
    // expression below goes false without Radix firing onOpenChange, so the
    // parent stays open and a primed auto-submit is never cancelled. Only
    // reachable on the form — the list is read-only past it.
    useEffect(() => {
        if (!open || step !== 'form' || items.length > 0) return;
        // An empty list means the reader unticked everything — unless the send
        // emptied it. A successful bulk send clears the selection while the
        // Secure Sign-In ask still holds the surface and `step` is still
        // 'form', and closing there dismissed the ask as a skip, reloaded the
        // page and left the reader with no confirmation for enquiries that had
        // just gone out.
        if (
            pendingSuccessRef.current ||
            gateRef.current?.isActive ||
            isResuming
        ) {
            return;
        }
        // The selection is sessionStorage-backed and syncs after hydration, so
        // an empty list on the first commit of a social return is 'not loaded
        // yet', not 'unticked'. Closing there cancelled nothing and sent
        // everything: the primed auto-submit fires with the modal gone.
        if (hasStoredBulkSelection()) return;
        handleCloseRef.current();
    }, [open, step, items.length, isResuming]);

    const handleAuthRequired = useCallback((publicUser: PublicUser) => {
        setGatePublicUser(publicUser);
        gateRef.current?.open({
            publicUser,
            flow:
                publicUser.type === 'new'
                    ? 'post-enquiry-sign-up'
                    : 'post-enquiry-login',
            message:
                publicUser.type === 'new'
                    ? 'Create an account to send your enquiries'
                    : 'Log in to send your enquiries',
        });
    }, []);

    // In-modal auth finished without a reload — clear the delay guard so the
    // auto-submit re-sends the selection now the session is authenticated.
    const handleAuthenticated = useCallback(
        (result?: { isSignUp: boolean }) => {
            setAuthedInModal(true);
            setAuthedIsSignUp(!!result?.isSignUp);
            setAutoSubmitDelay(false);
        },
        [setAutoSubmitDelay],
    );

    const gate = useAuthGateSegment({
        authSource: BULK_ENQUIRY_GATE_AUTH_SOURCE,
        onAuthenticated: handleAuthenticated,
        onResolved: handleOfferDone,
    });
    gateRef.current = gate;

    const handleClose = useCallback(() => {
        // Records the skip when leaving from the ask.
        gateRef.current?.close();
        // The selection has been sent — empty it and leave bulk mode, or the
        // island comes back offering a list that has already gone out.
        if (step === 'success' || pendingSuccessRef.current) exit();
        if (step !== 'success' && !pendingSuccessRef.current) {
            // Cancel the primed auto-submit so a later navigation does not
            // silently send the enquiries after the reader backed out.
            //
            // Mirrors the single-enquiry gate, and for the same two reasons the
            // old `gate.isActive` guard was wrong here too. The reachable exit
            // is the resuming step, where the gate has already resolved — and
            // that step only became closable once the dismissal hold started
            // expiring, so this hole opened at the same moment the trap was
            // fixed. It also has to sit above the `authedInModal` reload,
            // because authenticating in the modal is what produces the
            // resuming step, and the reload would carry the primed flag into
            // the next page. On bulk that means the whole selection sends.
            setEnquiryAutoSubmit(false);
            setAutoSubmitDelay(false);
        }
        if (authedInModal) {
            // Full reload for tracker / GTM reset parity with the redirect-based
            // auth path (the social round-trip already reloaded).
            reloadDocument();
            return;
        }
        onClose();
    }, [
        step,
        exit,
        authedInModal,
        onClose,
        setEnquiryAutoSubmit,
        setAutoSubmitDelay,
    ]);

    handleCloseRef.current = handleClose;

    // Continue the normal onboarding flow (full load — also resets trackers
    // when the account was created moments ago in this modal).
    const handleExplore = useCallback(() => {
        exit();
        assignDocumentLocation('/post-sign-up');
    }, [exit]);

    // The account is new when the in-modal auth step reported a signup, the
    // social round-trip returned from one, or the guest submit auto-created a
    // member (GeneratedMember in the response). The auth-wall snapshot is the
    // fallback — the user can switch email mid-auth, so what actually
    // authenticated wins.
    const isNewUser = useMemo(() => {
        if (authedInModal) return authedIsSignUp;
        if (socialSignUpReturn) return true;
        if (gatePublicUser?.type === 'new') return true;
        const data = fetcher.data;
        return !!(
            data &&
            'success' in data &&
            data.data?.__typename === 'CreateEnquiryResult' &&
            data.data.GeneratedMember
        );
    }, [
        authedInModal,
        authedIsSignUp,
        socialSignUpReturn,
        gatePublicUser?.type,
        fetcher.data,
    ]);

    // The backend only returns GeneratedMember when the submit itself created
    // the account. Authing in-modal or via social means one already existed, so
    // this is exactly the silently-created case.
    const accountAutoCreated = useMemo(() => {
        const data = fetcher.data;
        return !!(
            data &&
            'success' in data &&
            data.data?.__typename === 'CreateEnquiryResult' &&
            data.data.GeneratedMember
        );
    }, [fetcher.data]);

    // The form is shaped around a single professional, but a bulk enquiry names
    // no targets at all — the backend splits the items by owner and sends each
    // supplier only their own. This synthetic professional exists purely to
    // satisfy the form (tracking label, session key); it never routes anything.
    const professional = useMemo(
        () => ({
            ID: items[0]?.targetProfessionalId ?? 0,
            Title:
                supplierCount > 1 ? 'the selected suppliers' : 'the supplier',
            branchOptions: [],
        }),
        [items, supplierCount],
    );

    const itemType = useMemo(() => {
        const first = items[0]?.itemType ?? 'product';
        return items.every((item) => item.itemType === first)
            ? first
            : 'product';
    }, [items]);

    const siteTreeIDs = useMemo(
        () => items.map((item) => item.siteTreeID),
        [items],
    );

    const showSuccess = step === 'success';
    const showGate = !showSuccess && gate.isActive;
    const showResuming = !showSuccess && !showGate && isResuming;
    const showMobileList =
        isMobile &&
        mobileStep === 'list' &&
        !showSuccess &&
        !showGate &&
        !showResuming;
    /**
     * Nothing is in flight that the reader must wait through.
     *
     * `submitting`, not `!== idle`, and short-circuited by the confirmation:
     * the trailing `loading` is the route revalidation after the action, by
     * which point the enquiries are sent and the confirmation is on screen —
     * holding Close, swipe, and outside-tap shut through it strands the reader
     * in a modal whose work is done. Mirrors the single-enquiry gate.
     *
     * The hold also expires: a fetcher has no timeout of its own, so on a
     * stalled connection every exit — close, escape, outside tap and swipe —
     * stayed dead with a page reload the only way out. The single-enquiry gate
     * already bounded this; the bulk one did not.
     */
    const sendInFlight =
        !showSuccess && (showResuming || state === 'submitting');
    const dismissBlocked = useSendBlock(sendInFlight);
    const dismissable = !dismissBlocked;

    // Sending empties the selection, so the success pane reads from the
    // snapshot taken while the list still existed.
    // Editable only on the form. Past that the payload is already frozen in the
    // enquiry session, so unticking would prune the visible list while the
    // post-login resubmit still sent the original, larger selection.
    const selectionCommitted = step !== 'form';
    const selectionPane = showSuccess ? (
        <BulkSelectionPane
            items={sentItemsRef.current}
            supplierCount={sentSupplierCountRef.current}
            onRemove={remove}
            readOnly
            sent
        />
    ) : (
        <BulkSelectionPane
            items={items}
            supplierCount={supplierCount}
            onRemove={remove}
            readOnly={selectionCommitted}
        />
    );

    return (
        <TwoPaneModal
            corners="rounded"
            // Sending clears the selection, so an emptied list must not slam
            // the modal shut before the confirmation is read — nor while the
            // confirmation is still owed. A send that lands behind the Secure
            // Sign-In ask leaves `showSuccess` false until the ask resolves, so
            // the emptied list closed the modal, unmounted the ask, and took
            // with it the `onResolved` that was the only route to the success
            // screen. `pendingSuccess` and the live gate hold it open until
            // that hand-off has happened.
            open={
                open &&
                (items.length > 0 || showSuccess || pendingSuccess || showGate)
            }
            onOpenChange={(nextOpen) => {
                if (nextOpen) return;
                // Block dismissal while a submit / resubmit is in flight.
                if (!dismissable) return;
                handleClose();
            }}
            title={showSuccess ? 'Enquiries sent' : 'Send your enquiry list'}
            // A swipe never reaches onPointerDownOutside, so the sheet needs the
            // in-flight guard stated directly or a drag would dismiss a send.
            dismissible={dismissable}
            onPointerDownOutside={(e) => {
                if (!dismissable) {
                    e.preventDefault();
                }
            }}
            // The left pane stays intent-driven at every step, success
            // included: for bulk that is the list that was just sent, never
            // the default brand imagery.
            pane={selectionPane}
        >
            {/* The sheet drops the left pane, so on mobile the list is its own
                first step — it is the whole point of the screen and must not
                be what a small surface loses. Keyed off `isMobile` rather than
                `md:hidden` for the same reason the shell is: a phone in
                landscape gets the sheet while measuring wider than `md`. */}
            {showMobileList ? (
                <>
                    <BulkSelectionPane
                        items={items}
                        supplierCount={supplierCount}
                        onRemove={remove}
                        className="-mx-6 -mt-2 mb-6 size-auto bg-transparent"
                    />
                    <GateStickyFooter className="mt-auto">
                        <button
                            type="button"
                            onClick={() => setMobileStep('form')}
                            className="flex h-14 w-full items-center justify-center rounded-[var(--radius-btn-l)] bg-[#181818] text-[1.25rem] font-medium tracking-[-0.02em] text-[#f3f3f3] transition-colors hover:bg-[#2c2c2c]"
                        >
                            Next
                        </button>
                    </GateStickyFooter>
                </>
            ) : showSuccess ? (
                <BulkEnquirySuccess
                    variant={isNewUser ? 'new' : 'existing'}
                    supplierCount={sentSupplierCountRef.current}
                    accountAutoCreated={accountAutoCreated}
                    onClose={handleClose}
                    onExplore={handleExplore}
                />
            ) : showGate ? (
                gate.element
            ) : showResuming ? (
                <div className="mx-auto my-auto flex w-full max-w-[22.6875rem] flex-col items-center justify-center gap-4 py-16">
                    <Loader2 className="size-9 animate-spin text-[#828282]" />
                    <p className="text-[1.0625rem] text-[#828282]">
                        Sending your enquiries…
                    </p>
                </div>
            ) : (
                <>
                    {/* The form pane is where the action is, so it
                                    carries the modal's heading. Without it the
                                    selection pane's title is the only heading
                                    on screen and the list reads as the subject
                                    rather than as what is being sent. */}
                    <div className="mx-auto mb-6 w-full max-w-[23.4375rem]">
                        {isMobile ? (
                            <button
                                type="button"
                                aria-label="Back to your enquiry list"
                                onClick={() => setMobileStep('list')}
                                className="mb-4 flex size-8 items-center justify-center rounded-[0.75rem] text-[#181818] hover:bg-black/5"
                            >
                                <ArrowLeftLine className="size-5" />
                            </button>
                        ) : null}
                        <h2 className="m-0 text-2xl leading-[1.1] font-medium tracking-[-0.02em] text-[#181818]">
                            Send your enquiry
                        </h2>
                        <p className="m-0 mt-2 text-[0.9375rem] text-[#828282]">
                            {supplierCount === 1
                                ? 'One message to one supplier.'
                                : `One message, sent to each of your ${supplierCount} suppliers.`}
                        </p>
                    </div>
                    <EnquiryGateForm
                        professional={professional}
                        itemType={itemType}
                        siteTreeID={items[0]?.siteTreeID ?? 0}
                        siteTreeIDs={siteTreeIDs}
                        source="EnquiryModal"
                        includeBranch={false}
                        itemTitle={messageSubject(items, supplierCount)}
                        ctaLabel="Enquire"
                        onAuthRequired={handleAuthRequired}
                    />
                </>
            )}
        </TwoPaneModal>
    );
};
